import { getBeginningOfDay, getBeginningOfNextDay, parseDateTime, type IState } from "../helpers";
import { completeViolations } from "../hosCalculator";
import type { THosStatus, TViolationType } from "../../interfaces/statues";

/**
 * Emergency Response exemption — MYG-139401, Ontario O. Reg. 555/06 s.3.
 *
 * While the exemption is active the driver's clocks are floored to the time remaining until the
 * driver's day boundary and no violation is produced for exempt time. Time accumulation is
 * untouched: nothing is deferred, and nothing is added back later.
 *
 * The exemption is expressed by two control logs delivered by MYG-139399. They arrive in the
 * calculator's `exemptionLogs` parameter and never enter `state.logs`, because
 * `EngineCalculator.filterAndLocalizeHosLogs` routes every non-duty-status log into `controlLogs`.
 *
 * Everything the calculator needs is derived here, once per calculation, into an immutable list
 * of exempt periods. Deriving rather than tracking a mutable `active` flag is deliberate:
 *   - the control logs are not in `state.logs`, so a flag maintained while walking the log loop
 *     would never see them; and
 *   - the log loop rewinds (`state.i = 0`) when a split sleeper pairing completes, which would
 *     corrupt any flag mutated during iteration.
 *
 * A period never crosses a driver day boundary — that is what implements the automatic expiry.
 * The stored logs outlive that expiry on purpose: `calculateDailyViolations` re-derives previous
 * days' violations on every calculation, so the logs are pruned on the state's own retention
 * cutoff rather than at the day boundary (see `prunedEmergencyResponseLogsFor`).
 *
 * The file is in two halves. The first is pure: logs in, periods out, no engine types. The second
 * is the engine's whole interface to the feature, so that hosCalculator, availabilityDiscrete and
 * availabilityUI each need a single dispatching line rather than exemption logic of their own.
 */

export const EMERGENCY_RESPONSE_ENABLED: THosStatus = "EmergencyResponseExemptionEnabled";
export const EMERGENCY_RESPONSE_DISABLED: THosStatus = "EmergencyResponseExemptionDisabled";

/**
 * A half-open span of exempt time, `[start, end)`.
 *
 * `end` is the Disabled log's timestamp, or — when no Disabled log closed it — the driver's next
 * day boundary, which may be in the future relative to the moment being evaluated. That future
 * end is deliberate: it is what makes a still-running exemption swallow a whole candidate
 * violation window through the same clamp that trims a finished one.
 */
export interface IEmergencyResponsePeriod {
    start: Date;
    end: Date;
}

/* ---------------------------------------------------------------------------------------------
 * Deriving the exempt periods from the control logs
 * ------------------------------------------------------------------------------------------ */

/** True for either of the two Emergency Response control log types. */
export const isEmergencyResponseLog = (log: any): boolean =>
    log?.status === EMERGENCY_RESPONSE_ENABLED || log?.status === EMERGENCY_RESPONSE_DISABLED;

/**
 * Folds the Enabled/Disabled control logs into disjoint exempt periods.
 *
 * Tolerant of malformed sequences by design — nothing rejects a duplicate Enabled log (that
 * validation was punted to the rejected MYG-139400), `Set` has no validation hook, and ignored
 * logs are dropped upstream, so orphans reach the calculator in normal operation.
 */
export const buildEmergencyResponsePeriods = (
    logs: any[],
    toLocalTime: (date: Date) => Date,
    toUtcTime: (date: Date) => Date
): IEmergencyResponsePeriod[] => {
    // Sorted by dateTime alone, and stably. simplifyAndLocalizeLog drops the log's `sequence`
    // field, so the upstream compareDatesWithSequenceIds ordering survives only as array order —
    // any tie-break of our own would destroy a same-second toggle irrecoverably.
    const sorted = logs
        .filter(isEmergencyResponseLog)
        .map(log => ({ at: parseDateTime(log.dateTime), status: log.status }))
        .sort((a, b) => a.at.getTime() - b.at.getTime());

    const periods: IEmergencyResponsePeriod[] = [];
    let openedAt: Date | undefined;

    const close = (end: Date) => {
        if (openedAt && end.getTime() > openedAt.getTime()) {
            periods.push({ start: openedAt, end });
        }
        // A period that opens and closes at the same instant leaves no exempt time behind, so it
        // is dropped rather than kept as an empty span that could still close a live violation.
        openedAt = undefined;
    };

    for (const log of sorted) {
        if (openedAt) {
            const dayBoundary = getBeginningOfNextDay(openedAt, toLocalTime, toUtcTime);
            if (log.at.getTime() >= dayBoundary.getTime()) {
                // The exemption expired on its own before this log; whatever it says, it applies
                // to a new day. This is what makes "the exemption never spans days" true.
                close(dayBoundary);
            }
        }
        if (log.status === EMERGENCY_RESPONSE_ENABLED) {
            // A second Enabled log while one is already running neither restarts nor nests it.
            openedAt = openedAt ?? log.at;
        } else if (openedAt) {
            close(log.at);
        }
        // An orphan Disabled log — no exemption running — is ignored.
    }

    if (openedAt) {
        close(getBeginningOfNextDay(openedAt, toLocalTime, toUtcTime));
    }
    return periods;
};

/**
 * The exempt period containing `at`, or undefined when `at` was not exempt.
 *
 * This is the one primitive; the rest of the module derives from it. Only a period that
 * *contains* `at` counts, which is what lets a violation that began before the exemption opened
 * keep its original start instead of being clamped forward and silently erased.
 */
export const getEmergencyResponsePeriodCovering = (
    periods: IEmergencyResponsePeriod[],
    at: Date
): IEmergencyResponsePeriod | undefined =>
    periods.find(period => period.start.getTime() <= at.getTime() && at.getTime() < period.end.getTime());

/** The end of the exempt period containing `at`, or undefined when `at` was not exempt. */
export const getEmergencyResponseEndCovering = (periods: IEmergencyResponsePeriod[], at: Date): Date | undefined =>
    getEmergencyResponsePeriodCovering(periods, at)?.end;

/** True when `at` falls inside an exempt period. */
export const isEmergencyResponseActive = (periods: IEmergencyResponsePeriod[], at: Date): boolean =>
    getEmergencyResponsePeriodCovering(periods, at) !== undefined;

/** The start of a period opening within `[from, to]`, used to close in-flight violations at that instant. */
export const getEmergencyResponseStartInWindow = (periods: IEmergencyResponsePeriod[], from: Date, to: Date): Date | undefined => {
    const opening = periods.find(p => p.start.getTime() >= from.getTime() && p.start.getTime() <= to.getTime());
    return opening?.start;
};

/**
 * Drops logs before `dayStart`.
 *
 * Required, not an optimisation: unlike `adverseLogs` — which drains via `shift()` as it is
 * consumed — this array is only ever appended to on each incremental calculation, so without
 * pruning the persisted state blob grows without bound.
 */
export const pruneEmergencyResponseLogs = (logs: any[], dayStart: Date): any[] =>
    logs.filter(log => parseDateTime(log.dateTime).getTime() >= dayStart.getTime());

/* ---------------------------------------------------------------------------------------------
 * The engine's interface to the exemption
 *
 * Everything below takes IState so that no caller has to know how the exemption is represented.
 * Each is a single dispatching call from hosCalculator, availabilityDiscrete or availabilityUI.
 * ------------------------------------------------------------------------------------------ */

/**
 * Collects the control logs for `getState`, carrying forward whatever the previous state held.
 *
 * De-duplicated because a caller passing a rolling window re-supplies logs it has already sent,
 * and this array is retained for as long as the days it can affect are (see
 * `prunedEmergencyResponseLogsFor`) rather than being drained as it is consumed. The fold is
 * already idempotent under duplicates; this keeps the stored array from growing under them too.
 */
export const collectEmergencyResponseLogs = (exemptionLogs: any[], previous?: any[]): any[] => {
    const seen = new Set<string>();
    // parseDateTime, not the raw value: logs revived from the persisted state still carry string
    // dateTimes while incoming ones are Dates, and the same log in both forms must collide. Kept
    // total so a malformed log cannot throw here — this runs for every driver, flag on or off.
    const identity = (log: any) => {
        const at = parseDateTime(log?.dateTime);
        return `${log?.id ?? ""}|${at instanceof Date ? at.getTime() : String(log?.dateTime)}|${log?.status}`;
    };
    return (previous || []).concat(exemptionLogs.filter(isEmergencyResponseLog)).filter(log => {
        const key = identity(log);
        if (seen.has(key)) {
            return false;
        }
        seen.add(key);
        return true;
    });
};

/**
 * Derives the exempt periods for this calculation.
 *
 * Called once, before the log loop, and never re-derived — the loop rewinds to i = 0 when a split
 * sleeper pairing completes, which would corrupt anything maintained during iteration. Clearing the
 * derived periods and the persisted suppression marker when the release flag is off is the single
 * on/off point for the whole feature.
 */
export const initialiseEmergencyResponse = (state: IState): void => {
    if (!state.customCodes.enableEmergencyResponseExemption) {
        state.emergencyResponsePeriods = [];
        state.emergencyResponseSuppressedViolations = [];
        return;
    }
    state.emergencyResponsePeriods = buildEmergencyResponsePeriods(state.emergencyResponseLogs, state.toLocalTime, state.toUtcTime);
};

/**
 * The control logs worth persisting.
 *
 * The retention cutoff is the state's own — `truncateState`'s `fromDate`, floored to a driver day
 * — NOT the current driver day. `calculateDailyViolations` re-evaluates every day still held in
 * `state.days` on every calculation, and `isDailyViolationExemptedByEmergencyResponse` answers
 * from these logs, so a DailyOff/DailyRest violation suppressed on the day it was earned
 * reappears the moment the logs behind that suppression are gone. Retaining them exactly as long
 * as the days they can affect keeps an incremental chain agreeing with a single-shot calculation.
 *
 * `undefined` means the caller is keeping the whole state (`truncateState` prunes nothing in that
 * branch), so nothing is dropped here either. The current day's logs are always kept, whatever the
 * cutoff, so a live exemption can never be pruned out from under itself.
 */
export const prunedEmergencyResponseLogsFor = (state: IState, retainFrom: Date | undefined): any[] => {
    if (retainFrom === undefined) {
        return state.emergencyResponseLogs;
    }
    const currentDayStart = getBeginningOfDay(state.nextDate, state.toLocalTime, state.toUtcTime);
    return pruneEmergencyResponseLogs(state.emergencyResponseLogs, retainFrom.getTime() < currentDayStart.getTime() ? retainFrom : currentDayStart);
};

/**
 * The two independent boundaries that govern the Emergency Response floor at this calculation.
 *
 * `exemptionEnd` is controlled by the Enabled/Disabled logs. `driverDayBoundary` comes from the
 * driver's local day and is where the floor itself reaches zero. A manual Disabled log can make
 * the former earlier than the latter; they must never be used interchangeably.
 */
export interface IEmergencyResponseFloorBounds {
    exemptionEnd: Date;
    driverDayBoundary: Date;
}

export const getEmergencyResponseFloorBounds = (state: IState): IEmergencyResponseFloorBounds | undefined => {
    const exemptionEnd = getEmergencyResponseEndCovering(state.emergencyResponsePeriods, state.nextDate);
    if (exemptionEnd === undefined) {
        return undefined;
    }
    return {
        exemptionEnd,
        driverDayBoundary: getBeginningOfNextDay(state.nextDate, state.toLocalTime, state.toUtcTime)
    };
};

/**
 * Floors every availability in `availabilities` to the time left in the driver's day while the
 * exemption is active. Mutates in place; a no-op when the exemption is not active.
 *
 * Applying this to the discrete summary is enough to carry the floor into the aggregates, because
 * `min(max(T, a), max(T, b)) === max(T, min(a, b))`. availabilityUI applies it a second time to
 * catch the two limiters that bypass the summary; the operation is idempotent.
 *
 * Non-numeric entries (`restLabel`) are left alone, and `drivingBreakDuration` is never seen here
 * because it is attached after this runs — it is an elapsed duration, not an availability.
 */
export const applyEmergencyResponseFloor = (state: IState, availabilities: { [key: string]: any }): void => {
    const floorBounds = getEmergencyResponseFloorBounds(state);
    if (floorBounds === undefined) {
        return;
    }
    const floor = floorBounds.driverDayBoundary.getTime() - state.nextDate.getTime();
    for (const key of Object.keys(availabilities)) {
        if (typeof availabilities[key] === "number") {
            availabilities[key] = Math.max(availabilities[key], floor);
        }
    }
};


/* ---------------------------------------------------------------------------------------------
 * Keeping the "past this limit" signal alive across a suppressed violation
 *
 * `state.currentViolations` carries two meanings at once. It is the map of open violation records,
 * and — because the engine deletes a Workday/Driving/Duty entry the moment that clock's
 * availability returns to zero (`calculateWorkdayDrivingViolation` and its siblings) — it is also
 * the live answer to "is this driver past that limit right now?". Two paths read it as the
 * second thing: the shared split-completion gate in `splitSb/blockingViolations.ts` (used by both
 * the engine and the next-availability prediction), and the Reduced Rest eligibility check in
 * `exemptionHelpers.ts`.
 *
 * Suppressing a violation because the exemption is running is right for the record and wrong for
 * the flag: the driver's accumulated hours have not moved, so they are still past the limit.
 * Without the marker below, a sleeper pairing the ruleset blocks while the driver is in violation
 * completes instead, and the workday reset it produces outlives the exemption — which contradicts
 * this feature's own rule that nothing is deferred and nothing is added back later.
 *
 * The marker's lifecycle mirrors `currentViolations` exactly: set wherever the exemption leaves the
 * driver past a limit with no open record, cleared inside `completeViolations`, which is the one
 * path every genuine recovery goes through (availability returning to zero, and the workday reset
 * in `startNewWorkday`). The exemption's own paths re-mark immediately after their
 * `completeViolations` call, so ordering does the work and neither side has to know about the
 * other.
 * ------------------------------------------------------------------------------------------ */

/** Records that the exemption left `type` unreported while the driver was still past the limit. */
export const markEmergencyResponseSuppressedViolation = (state: IState, type: TViolationType): void => {
    if (state.emergencyResponseSuppressedViolations.indexOf(type) === -1) {
        state.emergencyResponseSuppressedViolations.push(type);
    }
};

/**
 * Forgets a suppressed violation, because the condition behind it has genuinely cleared.
 *
 * Called from `completeViolations` for every type it closes — including the exemption's own
 * closures, which re-mark straight afterwards.
 */
export const clearEmergencyResponseSuppressedViolation = (state: IState, type: TViolationType): void => {
    const at = state.emergencyResponseSuppressedViolations.indexOf(type);
    if (at !== -1) {
        state.emergencyResponseSuppressedViolations.splice(at, 1);
    }
};

/**
 * True when the exemption is hiding a violation of any of `types` that has not yet recovered.
 *
 * Callers OR this into their own `currentViolations` test rather than replacing it: the two answer
 * the same question about different halves of the state.
 */
export const hasEmergencyResponseSuppressedViolation = (state: IState, types: readonly TViolationType[]): boolean =>
    state.emergencyResponseSuppressedViolations.some(type => types.indexOf(type) !== -1);

/** Returns the non-exempt portions of a candidate half-open violation window. */
export const getNonExemptEmergencyResponseRanges = (
    periods: IEmergencyResponsePeriod[],
    fromDate: Date,
    toDate: Date
): IEmergencyResponsePeriod[] => {
    const ranges: IEmergencyResponsePeriod[] = [];
    let cursor = fromDate.getTime();
    const end = toDate.getTime();

    for (const period of periods) {
        if (period.end.getTime() <= cursor || period.start.getTime() >= end) {
            continue;
        }
        if (cursor < period.start.getTime()) {
            ranges.push({ start: new Date(cursor), end: new Date(Math.min(period.start.getTime(), end)) });
        }
        cursor = Math.max(cursor, period.end.getTime());
        if (cursor >= end) {
            break;
        }
    }
    if (cursor < end) {
        ranges.push({ start: new Date(cursor), end: new Date(end) });
    }
    return ranges;
};

/**
 * How a candidate violation window is affected by the exemption: `undefined` when it is not
 * affected at all and the caller should record it verbatim, otherwise the non-exempt pieces to
 * record in its place — which may be empty, meaning the window was wholly exempt.
 *
 * Distinguishing "not affected" from "wholly exempt" is what makes a zero-length window work.
 * `[t, t)` is an empty half-open span, so `getNonExemptEmergencyResponseRanges` correctly returns
 * nothing for it whether or not `t` is exempt — and a caller that recorded that result directly
 * would silently drop every violation whose window collapsed to a point, which happens whenever
 * two logs share a timestamp and `currentDate` meets `nextDate`. A point is therefore tested for
 * containment rather than overlap, and handed back to the caller when it is not exempt.
 *
 * Every violation the engine records comes through here, exemption or not, so the no-periods case
 * — the release flag off, or simply a driver who never armed the exemption — returns before
 * touching anything.
 */
export const splitViolationWindowForEmergencyResponse = (
    state: IState,
    fromDate: Date,
    toDate: Date
): IEmergencyResponsePeriod[] | undefined => {
    if (state.emergencyResponsePeriods.length === 0) {
        return undefined;
    }
    if (fromDate.getTime() === toDate.getTime()) {
        return isEmergencyResponseActive(state.emergencyResponsePeriods, fromDate) ? [] : undefined;
    }
    const overlaps = state.emergencyResponsePeriods.some(
        period => period.start.getTime() < toDate.getTime() && fromDate.getTime() < period.end.getTime()
    );
    return overlaps ? getNonExemptEmergencyResponseRanges(state.emergencyResponsePeriods, fromDate, toDate) : undefined;
};

/**
 * Records a candidate violation's non-exempt portions when Emergency Response affects its window.
 *
 * Returns `false` when the caller should record the original window normally. Returns `true` when
 * the exemption handled it, including when it suppressed the whole window.
 */
export const tryRecordEmergencyResponseAdjustedViolation = (
    state: IState,
    type: TViolationType,
    fromDate: Date,
    toDate: Date,
    recordViolationRange: (fromDate: Date, toDate: Date) => void
): boolean => {
    const nonExemptViolationRanges = splitViolationWindowForEmergencyResponse(state, fromDate, toDate);
    if (nonExemptViolationRanges === undefined) {
        return false;
    }

    nonExemptViolationRanges.forEach((range, index) => {
        recordViolationRange(range.start, range.end);
        const isIntermediateRange = index < nonExemptViolationRanges.length - 1;
        const isTrimmedAtEnd = range.end.getTime() < toDate.getTime();
        if (isIntermediateRange || isTrimmedAtEnd) {
            completeViolations(state, type, range.end);
        }
    });

    // The caller only reaches here while the driver is past this limit and driving. Whenever the
    // window's tail was exempt — the whole window, or everything after the last recorded range —
    // no record is left open, so `currentViolations` stops answering "past the limit" and the
    // marker has to. A window that ends on a recorded range needs nothing: its record is open.
    const lastRecorded = nonExemptViolationRanges[nonExemptViolationRanges.length - 1];
    if (!lastRecorded || lastRecorded.end.getTime() < toDate.getTime()) {
        markEmergencyResponseSuppressedViolation(state, type);
    }
    return true;
};

/**
 * Closes any violation already running at the instant the driver arms the exemption.
 *
 * Needed alongside the exempt-range split `startViolation` performs via
 * `getNonExemptEmergencyResponseRanges`, not instead of it. The violation path reads unfloored
 * availability on purpose — flooring it would reach nextHosEventDate, which schedules
 * recalculation — so calculateViolations would otherwise keep an open violation accruing right
 * through the exempt window. Closing it here keeps the pre-exemption time on the record while the
 * range split stops it being reopened.
 *
 * Idempotent under the log-loop rewind: completeViolations no-ops when nothing is open, and
 * re-marking a type already marked is a no-op too.
 */
export const closeViolationsInterruptedByEmergencyResponse = (state: IState): void => {
    const exemptStart = getEmergencyResponseStartInWindow(state.emergencyResponsePeriods, state.currentDate, state.nextDate);
    if (!exemptStart) {
        return;
    }
    const violationTypes = Object.keys(state.currentViolations) as TViolationType[];
    for (const violationType of violationTypes) {
        const violation = state.currentViolations[violationType];
        if (violation && violation.fromDate.getTime() < exemptStart.getTime()) {
            completeViolations(state, violationType, exemptStart);
            markEmergencyResponseSuppressedViolation(state, violationType);
        }
    }
};

/**
 * The next named transition in displayed availability while the Emergency Response floor governs.
 * `exemptionEnd` is a manual Disabled-log boundary; `driverDayBoundary` is the floor's own zero.
 * A raw clock can also surface before either when the falling floor meets it.
 */
export type TEmergencyResponseTransition =
    | { factorName: "emergencyResponseExemptionEnd"; label: "emergency response exemption ends"; date: Date }
    | { factorName: "emergencyResponseFloorRelease"; label: "emergency response floor releases"; date: Date }
    | { factorName: "emergencyResponseDriverDayBoundary"; label: "emergency response day boundary"; date: Date };

export interface IEmergencyResponseProjection {
    /** The next exact change in the availability shown to the driver, if one can be derived. */
    transition: TEmergencyResponseTransition | undefined;
    /** When the availability shown to the driver reaches zero, if that moment can be derived. */
    exhaustion: Date | undefined;
}

/** How the underlying, unfloored value behaves from now on. See getEmergencyResponseProjection. */
export type TUnderlyingTrajectory =
    /** Falling at −1 indefinitely; the floor falls with it and never catches up. */
    | { kind: "draining" }
    /** Held at the current value; the falling floor meets it. */
    | { kind: "frozen" }
    /** Falling at −1 until `at`, then held at `value`. */
    | { kind: "frozenFrom"; at: Date; value: number }
    /** Not derivable from this state — the calculator answers with a coverage gap, so this does too. */
    | { kind: "unknown" };

export const DRAINING: TUnderlyingTrajectory = { kind: "draining" };
export const FROZEN: TUnderlyingTrajectory = { kind: "frozen" };
export const UNKNOWN_RATE: TUnderlyingTrajectory = { kind: "unknown" };

const laterOf = (first: Date, second: Date): Date =>
    new Date(Math.max(first.getTime(), second.getTime()));

export const getEmergencyResponseProjection = (
    state: IState,
    availability: number | undefined,
    trajectory: TUnderlyingTrajectory,
    underlyingExhaustion: Date | undefined
): IEmergencyResponseProjection | undefined => {
    if (availability === undefined) {
        return undefined;
    }
    const floorBounds = getEmergencyResponseFloorBounds(state);
    if (floorBounds === undefined) {
        return undefined;
    }

    const { exemptionEnd, driverDayBoundary } = floorBounds;
    const floor = driverDayBoundary.getTime() - state.nextDate.getTime();
    if (availability >= floor) {
        return undefined;
    }

    const exemptionEndsBeforeDriverDayBoundary = exemptionEnd.getTime() < driverDayBoundary.getTime();
    const endTransition: TEmergencyResponseTransition = exemptionEndsBeforeDriverDayBoundary
        ? { factorName: "emergencyResponseExemptionEnd", label: "emergency response exemption ends", date: exemptionEnd }
        : { factorName: "emergencyResponseDriverDayBoundary", label: "emergency response day boundary", date: driverDayBoundary };

    // An explicit Disabled log makes the raw clock visible before the floor reaches zero. A raw
    // zero-crossing that was hidden before that point becomes visible at the exemption end instead.
    const exhaustionAtExemptionEnd = (): Date | undefined => {
        if (!exemptionEndsBeforeDriverDayBoundary) {
            return driverDayBoundary;
        }
        if (underlyingExhaustion !== undefined) {
            return laterOf(exemptionEnd, underlyingExhaustion);
        }
        if (availability <= 0) {
            return exemptionEnd;
        }
        if (trajectory.kind === "frozenFrom"
            && trajectory.at.getTime() < exemptionEnd.getTime()
            && trajectory.value <= 0) {
            return exemptionEnd;
        }
        // DRAINING describes the current rate, not a durable raw zero-crossing. Rest deliberately
        // returns no underlying exhaustion while an off-duty break can qualify and lift its clock;
        // deriving nextDate + availability here would promise a zero beyond that transition. Leave
        // it undefined and let the exemption-end transition trigger the required re-evaluation.
        return undefined;
    };

    const transitionAtEnd = (): IEmergencyResponseProjection => ({
        transition: endTransition,
        exhaustion: exhaustionAtExemptionEnd()
    });
    const floorReleaseAt = (value: number): TEmergencyResponseTransition => ({
        factorName: "emergencyResponseFloorRelease",
        label: "emergency response floor releases",
        date: new Date(driverDayBoundary.getTime() - Math.max(0, value))
    });
    const transitionBeforeEnd = (transition: TEmergencyResponseTransition): IEmergencyResponseProjection =>
        transition.date.getTime() < endTransition.date.getTime()
            ? { transition, exhaustion: undefined }
            : transitionAtEnd();

    switch (trajectory.kind) {
        case "draining":
            return transitionAtEnd();
        case "frozen":
            return transitionBeforeEnd(floorReleaseAt(availability));
        case "frozenFrom": {
            if (trajectory.at.getTime() >= endTransition.date.getTime()) {
                return transitionAtEnd();
            }
            const floorRelease = floorReleaseAt(trajectory.value);
            const releaseAtFreeze = floorRelease.date.getTime() < trajectory.at.getTime()
                ? { ...floorRelease, date: trajectory.at }
                : floorRelease;
            return transitionBeforeEnd(releaseAtFreeze);
        }
        case "unknown":
            // The calculator's own UNKNOWN_* factor forces immediate re-evaluation. It is unsafe
            // to invent a floor release, and a manual end is an event rather than proof of zero.
            return {
                transition: undefined,
                exhaustion: exemptionEndsBeforeDriverDayBoundary ? undefined : driverDayBoundary
            };
    }
};

/**
 * True when the floor will still be governing this availability at `at`, so an inflection the
 * calculator would otherwise report there cannot show through to the driver.
 *
 * `bestCaseAvailabilityAt` is the highest the underlying value could be at `at`. The floor falls at
 * −1 toward the driver's actual day boundary, so it is `driverDayBoundary − at`; when the best case
 * is still under that, the reported value is the floor either side of the inflection and reporting it
 * would be spurious. The floor ends at `exemptionEnd`, however, so nothing is masked at or after a
 * manual Disabled log.
 */
export const isMaskedByEmergencyResponseFloor = (state: IState, at: Date, bestCaseAvailabilityAt: number): boolean => {
    const floorBounds = getEmergencyResponseFloorBounds(state);
    if (floorBounds === undefined || at.getTime() >= floorBounds.exemptionEnd.getTime()) {
        return false;
    }
    return bestCaseAvailabilityAt < floorBounds.driverDayBoundary.getTime() - at.getTime();
};

/**
 * True when a whole-day violation should be suppressed.
 *
 * DailyOff and DailyRest never reach `startViolation`, so they are handled by their own question.
 * The point of no return is the moment the day's requirement became unachievable: if that fell
 * inside the exemption there is nothing to report, and if it fell before the driver armed the
 * exemption the day was already lost and the violation still stands.
 */
export const isDailyViolationExemptedByEmergencyResponse = (state: IState, pointOfNoReturnDate: Date): boolean =>
    isEmergencyResponseActive(state.emergencyResponsePeriods, pointOfNoReturnDate);
